import {
  type DateTimeRangeStrictValueType,
  type DateTimeValueStrictType,
} from "@validereinc/utilities";
import { isPlainObject } from "es-toolkit/compat";
import type { ReactNode } from "react";
import type { FieldValues } from "react-hook-form";
import type {
  CommonComponentProps,
  ComponentPropsForElement,
  ControlledInputComponentProps,
  InputComponentProps,
  SituationalComponentProps,
} from "../../utils/component-props";
import type { InputWithLabelsExposedProps } from "../_components/InputWithLabels/types";

export type DateValue = DateTimeValueStrictType;
export type DateRangeValue = DateTimeRangeStrictValueType;
export type DateValueOrRange = DateValue | DateRangeValue;
export type DateVariant = "time" | "day" | "week" | "month" | "year";
export type SingleDateVariant = "time" | "day";
export type DateRangeVariant = "week" | "month" | "year";
export type SingleDateValue =
  | { variant: "day" | "time"; value?: Date }
  | { variant: "week" | "month" | "year"; value?: DateRangeValue };

// IMPROVE: the types for this component are very messy and likely incorrect in several places - could use a refactor in the future

type CommonProps = Partial<CommonComponentProps> &
  Omit<InputComponentProps<"div">, "value" | "onChange"> &
  Pick<
    SituationalComponentProps,
    "error" | "invalidMessage" | "isInline" | "isFluid"
  > & {
    min?: Date;
    max?: Date;
    hasNextPrevButtons?: boolean;
    isClearable?: boolean;
  };

export type DateSelectorProps = CommonProps & {
  isRange?: boolean;
  isArraySyntax?: boolean;
  variant: DateVariant;
  value?: Date | DateRangeValue | [Date, Date];
  onChange?: (newValue: DateValueOrRange | undefined) => void;
  /** optional flair to display within the input */
  flairSlot?:
    | ((
        props: Pick<ComponentPropsForElement<"div">, "className" | "style">
      ) => ReactNode)
    | null;
  /** optional left slot to display next to the input */
  leftSlot?:
    | ((
        props: Pick<ComponentPropsForElement<"div">, "className" | "style">
      ) => ReactNode)
    | null;
};

export type DateSelectorValueProps =
  | ({
      isRange: false;
      isMenuOpen: boolean;
      isClearable?: boolean;
    } & SingleDateValue &
      Pick<CommonProps, "placeholder">)
  | ({
      isRange: true;
      variant: DateVariant;
      value: DateRangeValue;
      isMenuOpen: boolean;
      isClearable?: boolean;
    } & Pick<CommonProps, "placeholder">);

export type DateSelectorMenuContentProps = {
  onClose: () => void;
  isClearable?: boolean;
  min?: Date;
  max?: Date;
  setContentRef: (node: HTMLDivElement) => void;
} & (
  | ({ isRange: false } & SingleDateValue &
      (
        | {
            variant: "day" | "time";
            onChange: (newValue: DateValue | undefined) => void;
          }
        | {
            variant: "week" | "month" | "year";
            onChange: (newValue: DateRangeValue | undefined) => void;
          }
      ))
  | {
      isRange: true;
      variant: DateVariant;
      value: DateRangeValue;
      onChange: (newValue: DateRangeValue | undefined) => void;
    }
);

type CommonDatePickerProps = Pick<CommonProps, "min" | "max"> & {
  onClose: () => void;
  isRange: boolean;
  isClearable?: boolean;
};

export type DayPickerProps = CommonDatePickerProps & {
  isWithTime?: boolean;
} & (
    | {
        isRange: false;
        value?: DateValue;
        onChange: (newValue: DateValue | undefined) => void;
      }
    | {
        isRange: true;
        value?: DateRangeValue;
        onChange: (newValue: DateRangeValue | undefined) => void;
      }
  );

export type RangePickerProps = CommonDatePickerProps & {
  value?: DateRangeValue;
  onChange: (newValue: DateRangeValue | undefined) => void;
};

export const isRangeValue = (
  dateOrDateRange?: DateValueOrRange | [Date, Date]
): dateOrDateRange is DateRangeValue => {
  if (
    !dateOrDateRange ||
    dateOrDateRange instanceof Date ||
    Array.isArray(dateOrDateRange) ||
    !isPlainObject(dateOrDateRange)
  ) {
    return false;
  }

  return Object.keys(dateOrDateRange).includes("from");
};

export type TimePickerProps = {
  value: Date;
  onChange: (newValue: Date | undefined) => void;
  onClose: () => void;
  isStart?: boolean;
  isClearable?: boolean;
};

export type ControlledDateSelectorInputProps<T extends FieldValues> = Omit<
  ControlledInputComponentProps<T, DateSelectorProps>,
  "defaultValue"
> &
  InputWithLabelsExposedProps & {
    defaultValue?: DateValueOrRange;
  };
